import { HttpError } from '../../config/Axios/axios-instance';
import * as actionTypes from './actionTypes';
import { EventRegistrationActionTypes } from './actionTypes';
import { EventRegistration } from '../../domain/EventRegistration';
import { ListResults } from '../../common/List/List';

export interface EventRegistrationStateType {
  eventRegistrationsList: ListResults<EventRegistration> | null;
  eventRegistrationsListLoading: boolean;
  eventRegistrationsListError: HttpError | null;
  eventRegistrationsListUpdateNeeded: boolean;

  createdEventRegistrationId: number | null;
  createEventRegistrationLoading: boolean;
  createEventRegistrationSuccess: boolean;
  createEventRegistrationError: HttpError | null;

  updateEventRegistrationLoading: boolean;
  updateEventRegistrationError: HttpError | null;
  updateEventRegistrationSuccess: boolean;
  updatedEventRegistration: EventRegistration | null;

  deleteMultipleEventRegistrationsLoading: boolean;
  deleteMultipleEventRegistrationsError: HttpError | null;
  deleteMultipleEventRegistrationsSuccess: boolean;

  deleteEventRegistrationLoading: boolean;
  deleteEventRegistrationError: HttpError | null;
  deleteEventRegistrationSuccess: boolean;
}

export type EventRegistrationActionType = EventRegistrationStateType & {
  type: EventRegistrationActionTypes;
};

export const initialState: EventRegistrationStateType = {
  eventRegistrationsList: null,
  eventRegistrationsListLoading: true,
  eventRegistrationsListError: null,
  eventRegistrationsListUpdateNeeded: false,

  createdEventRegistrationId: null,
  createEventRegistrationLoading: false,
  createEventRegistrationSuccess: false,
  createEventRegistrationError: null,

  updateEventRegistrationLoading: false,
  updateEventRegistrationError: null,
  updateEventRegistrationSuccess: false,
  updatedEventRegistration: null,

  deleteMultipleEventRegistrationsLoading: false,
  deleteMultipleEventRegistrationsError: null,
  deleteMultipleEventRegistrationsSuccess: false,

  deleteEventRegistrationLoading: false,
  deleteEventRegistrationError: null,
  deleteEventRegistrationSuccess: false,
};

const fetchEventRegistrationsStart = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  eventRegistrationsListLoading: true,
  eventRegistrationsListError: null,
});

const fetchEventRegistrationsSuccess = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  eventRegistrationsList: action.eventRegistrationsList,
  eventRegistrationsListLoading: false,
  eventRegistrationsListError: null,
  eventRegistrationsListUpdateNeeded: false,
});

const fetchEventRegistrationsFail = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  eventRegistrationsList: null,
  eventRegistrationsListLoading: false,
  eventRegistrationsListError: action.eventRegistrationsListError,
  eventRegistrationsListUpdateNeeded: false,
});

export const resetFetchEventRegistrationsStore = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  eventRegistrationsList: null,
  eventRegistrationsListLoading: false,
  eventRegistrationsListError: null,
  eventRegistrationsListUpdateNeeded: false,
});

const createEventRegistrationStart = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  createdEventRegistrationId: null,
  createEventRegistrationLoading: true,
  createEventRegistrationError: null,
  createEventRegistrationSuccess: false,
});

const createEventRegistrationSuccess = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  createdEventRegistrationId: action.createdEventRegistrationId,
  createEventRegistrationLoading: false,
  createEventRegistrationError: null,
  createEventRegistrationSuccess: true,
});

const createEventRegistrationFail = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  createdEventRegistrationId: null,
  createEventRegistrationLoading: false,
  createEventRegistrationError: action.createEventRegistrationError,
  createEventRegistrationSuccess: false,
});

const resetCreateEventRegistrationStore = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  createdEventRegistrationId: null,
  createEventRegistrationLoading: false,
  createEventRegistrationSuccess: false,
  createEventRegistrationError: null,
});

export const updateEventRegistrationStart = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  updateEventRegistrationLoading: true,
  updateEventRegistrationError: null,
  updateEventRegistrationSuccess: false,
  updatedEventRegistration: null,
});

export const updateEventRegistrationSuccess = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  updateEventRegistrationLoading: false,
  updateEventRegistrationError: null,
  updateEventRegistrationSuccess: true,
  updatedEventRegistration: action.updatedEventRegistration,
  eventRegistrationsListUpdateNeeded: true,
});

export const updateEventRegistrationFail = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  updateEventRegistrationLoading: false,
  updateEventRegistrationError: action.updateEventRegistrationError,
  updateEventRegistrationSuccess: false,
  updatedEventRegistration: null,
});

export const resetUpdateEventRegistrationStore = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  updateEventRegistrationLoading: false,
  updateEventRegistrationError: null,
  updateEventRegistrationSuccess: false,
  updatedEventRegistration: null,
});

export const deleteMultipleEventRegistrationsStart = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  deleteMultipleEventRegistrationsLoading: true,
});

const deleteMultipleEventRegistrationsSuccess = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  deleteMultipleEventRegistrationsLoading: false,
  deleteMultipleEventRegistrationsError: null,
  deleteMultipleEventRegistrationsSuccess: true,
  eventRegistrationsListUpdateNeeded: true,
});

const deleteMultipleEventRegistrationsFail = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  deleteMultipleEventRegistrationsLoading: false,
  deleteMultipleEventRegistrationsError:
    action.deleteMultipleEventRegistrationsError,
});

export const resetDeleteMultipleEventRegistrationsStore = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  deleteMultipleEventRegistrationsLoading: false,
  deleteMultipleEventRegistrationsError: null,
  deleteMultipleEventRegistrationsSuccess: false,
});

const deleteEventRegistrationStart = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  deleteEventRegistrationLoading: true,
});

const deleteEventRegistrationSuccess = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  deleteEventRegistrationLoading: false,
  deleteEventRegistrationError: null,
  deleteEventRegistrationSuccess: true,
  eventRegistrationsListUpdateNeeded: true,
});

const deleteEventRegistrationFail = (
  state: EventRegistrationStateType,
  action: EventRegistrationActionType,
): EventRegistrationStateType => ({
  ...state,
  deleteEventRegistrationLoading: false,
  deleteEventRegistrationError: action.deleteEventRegistrationError,
});

const resetDeleteEventRegistrationStore = (
  state: EventRegistrationStateType,
): EventRegistrationStateType => ({
  ...state,
  deleteEventRegistrationLoading: false,
  deleteEventRegistrationError: null,
  deleteEventRegistrationSuccess: false,
});

const resetEventRegistrationStore = (): EventRegistrationStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: EventRegistrationActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_EVENT_REGISTRATIONS_START:
      return fetchEventRegistrationsStart(state);
    case actionTypes.FETCH_EVENT_REGISTRATIONS_SUCCESS:
      return fetchEventRegistrationsSuccess(state, action);
    case actionTypes.FETCH_EVENT_REGISTRATIONS_FAIL:
      return fetchEventRegistrationsFail(state, action);
    case actionTypes.RESET_FETCH_EVENT_REGISTRATIONS_STORE:
      return resetFetchEventRegistrationsStore(state);
    case actionTypes.CREATE_EVENT_REGISTRATION_START:
      return createEventRegistrationStart(state);
    case actionTypes.CREATE_EVENT_REGISTRATION_SUCCESS:
      return createEventRegistrationSuccess(state, action);
    case actionTypes.CREATE_EVENT_REGISTRATION_FAIL:
      return createEventRegistrationFail(state, action);
    case actionTypes.RESET_CREATE_EVENT_REGISTRATION_STORE:
      return resetCreateEventRegistrationStore(state);
    case actionTypes.UPDATE_EVENT_REGISTRATION_START:
      return updateEventRegistrationStart(state);
    case actionTypes.UPDATE_EVENT_REGISTRATION_SUCCESS:
      return updateEventRegistrationSuccess(state, action);
    case actionTypes.UPDATE_EVENT_REGISTRATION_FAIL:
      return updateEventRegistrationFail(state, action);
    case actionTypes.RESET_UPDATE_EVENT_REGISTRATION_STORE:
      return resetUpdateEventRegistrationStore(state);
    case actionTypes.DELETE_MULTIPLE_EVENT_REGISTRATIONS_START:
      return deleteMultipleEventRegistrationsStart(state);
    case actionTypes.DELETE_MULTIPLE_EVENT_REGISTRATIONS_SUCCESS:
      return deleteMultipleEventRegistrationsSuccess(state);
    case actionTypes.DELETE_MULTIPLE_EVENT_REGISTRATIONS_FAIL:
      return deleteMultipleEventRegistrationsFail(state, action);
    case actionTypes.RESET_DELETE_MULTIPLE_EVENT_REGISTRATIONS_STORE:
      return resetDeleteMultipleEventRegistrationsStore(state);
    case actionTypes.DELETE_EVENT_REGISTRATION_START:
      return deleteEventRegistrationStart(state);
    case actionTypes.DELETE_EVENT_REGISTRATION_SUCCESS:
      return deleteEventRegistrationSuccess(state);
    case actionTypes.DELETE_EVENT_REGISTRATION_FAIL:
      return deleteEventRegistrationFail(state, action);
    case actionTypes.RESET_DELETE_EVENT_REGISTRATION_STORE:
      return resetDeleteEventRegistrationStore(state);
    case actionTypes.RESET_EVENT_REGISTRATION_STORE:
      return resetEventRegistrationStore();
    default:
      return state;
  }
};

export default reducer;
